import { DataKind, ShiftSummaryCategory } from "../../../reviewAndPublish/model";
import { isActual } from "../../../../utils/midShiftActuals";

/**
 * Shared process-path row derivation for a shiftSummary category, used by BOTH
 * the on-screen Process Path table (ShiftDataTable) and the CSV export
 * (downloadPlanCsv) so their rows/ordering/totals can't silently drift.
 *
 * Rows are one per unique process-path label, with the numeric value per time
 * slot (0 when the label is absent in a slot), sorted by the first projected
 * time-slot value descending. `totals` is the per-slot column sum.
 */
export interface ProcessPathRow {
  label: string;
  values: number[];
}

export interface ProcessPathData {
  /** Formatted "HH:MM - HH:MM" column labels, one per time slot. */
  timeSlots: string[];
  /**
   * Actual-vs-projected tag per time slot, aligned with timeSlots. ACTUAL for an elapsed
   * mid-shift hour; PROJECTED otherwise (including when the backend omits the tag).
   */
  dataKinds: DataKind[];
  /** Process-path rows, sorted by the first projected time-slot value descending. */
  rows: ProcessPathRow[];
  /** Column sum per time slot (across all rows). */
  totals: number[];
}

export const buildProcessPathData = (category: ShiftSummaryCategory): ProcessPathData => {
  const planOutput = category.planOutput;
  const timeSlots = planOutput.map((slot) => `${slot.startTime} - ${slot.endTime}`);
  const dataKinds = planOutput.map((slot) => (isActual(slot) ? DataKind.ACTUAL : DataKind.PROJECTED));

  // Rows are ordered by the plan, not by what already happened: on a mid-shift re-plan the
  // leading columns are elapsed (actual) hours, where a function nobody worked yet reads 0.
  // Falls back to the first column when every slot is actual.
  const firstProjectedIndex = dataKinds.indexOf(DataKind.PROJECTED);
  const sortIndex = firstProjectedIndex === -1 ? 0 : firstProjectedIndex;

  // Unique process-path labels across all time slots.
  const labels: string[] = [];
  planOutput.forEach((slot) => {
    slot.data.forEach((item) => {
      if (!labels.includes(item.label)) {
        labels.push(item.label);
      }
    });
  });

  const rows: ProcessPathRow[] = labels
    .map((label) => ({
      label,
      // Value per slot; 0 when the label is absent in that slot.
      values: planOutput.map((slot) => {
        const entry = slot.data.find((item) => item.label === label);
        return entry ? entry.intValue : 0;
      }),
    }))
    // Sort by the first projected time-slot value, descending. planOutput is
    // non-empty here, so values[sortIndex] is always present.
    .sort((a, b) => b.values[sortIndex] - a.values[sortIndex]);

  const totals = timeSlots.map((_, i) => rows.reduce((sum, row) => sum + row.values[i], 0));

  return { timeSlots, dataKinds, rows, totals };
};
